export enum ReporterSections {
  General = 'general',
  ECDN11 = 'ecdn11'
}

export const CONDITION_CRITICAL = {};

export const CONDITION_EXPEDITE = {
  CRITICAL: 'critical'
};

export type MetadataTag = 'rate' | 'dur' | 'pos' | 'b';
interface ProtoEvent {
  code: number;
  metadata?: MetadataTag;
}

export interface EventObject extends ProtoEvent {
  name: MonitoredPlayerEvent;
}

// Add name field on each value identical to its key
const addNameField = (
  events: Record<string, ProtoEvent>
): Readonly<Record<string, Readonly<EventObject>>> =>
  Object.freeze(
    Object.getOwnPropertyNames(events).reduce((result, n) => {
      const name = n as MonitoredPlayerEvent;
      result[name] = Object.freeze({ name, ...events[name] });
      return result;
    }, {} as Record<string, EventObject>)
  );

export type MonitoredVideoEvent =
  | 'canplay'
  | 'canplaythrough'
  | 'complete'
  | 'durationchange'
  | 'emptied'
  | 'ended'
  | 'loadeddata'
  | 'loadedmetadata'
  | 'pause'
  | 'play'
  | 'playing'
  | 'ratechange'
  | 'seeked'
  | 'seeking'
  | 'stalled'
  | 'suspend'
  | 'waiting';

export type StdPlayerEventType = Readonly<
  Record<MonitoredVideoEvent, Readonly<EventObject>>
>;
export const StdPlayerEvents: StdPlayerEventType = addNameField({
  canplay: { code: 1 },
  canplaythrough: { code: 2 },
  complete: { code: 3 },
  durationchange: { code: 4, metadata: 'dur' },
  emptied: { code: 5 },
  ended: { code: 6 },
  loadeddata: { code: 7 },
  loadedmetadata: { code: 8 },
  pause: { code: 9 },
  play: { code: 10 },
  playing: { code: 11 },
  ratechange: { code: 12, metadata: 'rate' },
  seeked: { code: 13, metadata: 'pos' },
  seeking: { code: 14, metadata: 'pos' },
  stalled: { code: 15 },
  suspend: { code: 16 },
  waiting: { code: 17 }
});

export type MonitoredNonVideoEvent =
  | 'beforeunload'
  | 'playerdispose'
  | 'playbackbitratechanged'
  | 'visibilitychange'
  | 'playbackstarted'
  | 'pagehide';

export type MonitoredPlayerEvent = MonitoredNonVideoEvent | MonitoredVideoEvent;

type NonStdPlayerEventType = Readonly<
  Record<MonitoredNonVideoEvent, Readonly<EventObject>>
>;
export const NonStdPlayerEvents: NonStdPlayerEventType = addNameField({
  beforeunload: { code: 101 },
  playerdispose: { code: 102 },
  playbackbitratechanged: { code: 103, metadata: 'b' }, // AMP player
  visibilitychange: { code: 104 },
  pagehide: { code: 105 },
  playbackstarted: { code: 106 }
});

export const PLAYER_EVENTS_BY_CODE: Record<number, EventObject> = Object.values(
  { ...NonStdPlayerEvents, ...StdPlayerEvents }
).reduce((acc: Record<number, EventObject>, value) => {
  acc[value.code] = value;
  return acc;
}, {});

export const LIVE_DURATION = -1;
